import {
  ChangeEvent,
  forwardRef,
  Fragment,
  InputHTMLAttributes,
  KeyboardEvent,
  memo,
  ReactNode,
  SyntheticEvent,
} from 'react';
import cls from 'classnames';

import Button, { SIZE_XSMALL } from '@/common/components/Button/Button';
import Icon from '@/common/components/Icon/Icon';

import { IconName } from '../../Icon/iconName';

import styles from './FancyInput.module.css';

type FancyInputAppearanceType = 'error' | 'success';

const CLASS_TEXT_COLOR_MAP: Record<FancyInputAppearanceType, string> = {
  error: styles.fancyInputErrorText,
  success: styles.fancyInputSuccessText,
};

export interface FancyInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onBlur'> {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string, e: ChangeEvent<HTMLInputElement>, normalize?: FancyInputProps['normalize']) => void;
  onBlur?: (value: string, e: ChangeEvent<HTMLInputElement>, normalize?: FancyInputProps['normalize']) => void;
  className?: string;
  onClear?: () => void;
  // TODO "string" should be removed, but we need to refactor other code
  appearance?: FancyInputAppearanceType | string;
  helperText?: ReactNode;
  normalize?: (value: string) => string;
  suffix?: ReactNode;
  endIcon?: IconName;
  startIcon?: IconName;
  onClickEndIcon?: (e: SyntheticEvent) => void;
  onClickStartIcon?: (e: SyntheticEvent) => void;
  contentClassName?: string;
  startIconAriaLabel?: string;
  endIconAriaLabel?: string;
}

const FancyInput = forwardRef<HTMLInputElement, FancyInputProps>((props, ref) => {
  const {
    value,
    defaultValue,
    onChange,
    onBlur,
    onClear,
    appearance,
    helperText,
    normalize,
    suffix,
    endIcon,
    startIcon,
    onClickEndIcon,
    onClickStartIcon,
    contentClassName,
    startIconAriaLabel,
    endIconAriaLabel,
    onKeyDown,
    ...rest
  } = props;

  let sanitizedValue = value ?? defaultValue;

  if ((value === null || value === undefined) && defaultValue === null) {
    sanitizedValue = '';
  }

  function onBlurHandler(e: ChangeEvent<HTMLInputElement>) {
    onBlur?.(e.target.value, e, normalize);
  }

  function onChangeHandler(e: ChangeEvent<HTMLInputElement>) {
    onChange?.(e.target.value, e, normalize);
  }

  function onKeyDownHandler(e: KeyboardEvent<HTMLInputElement>) {
    // avoid bubbling event in case if other listeners have reaction on key down event,
    // pressing arrow keys will have effect only for input
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      e.stopPropagation();
    }

    onKeyDown?.(e);
  }

  return (
    <Fragment>
      <div
        className={cls(
          styles.fancyInput,
          appearance && CLASS_TEXT_COLOR_MAP[appearance as FancyInputAppearanceType],
          {
            [styles.fancyInputHasIcon]: endIcon || startIcon,
            [styles.fancyInputHasSuffix]: !!suffix,
          },
          contentClassName,
        )}
      >
        {startIcon && (
          <Button
            aria-label={startIconAriaLabel}
            className={styles.fancyInputIcon}
            color="transparent"
            onClick={onClickStartIcon}
            withIcon
          >
            <Icon name={startIcon} />
          </Button>
        )}

        <input
          ref={ref}
          {...rest}
          onBlur={onBlurHandler}
          onChange={onChangeHandler}
          onKeyDown={onKeyDownHandler}
          value={sanitizedValue}
        />

        {onClear && value && (
          <div className={styles.clear}>
            <Button aria-label="Clear input" color="transparent" onClick={onClear} size={SIZE_XSMALL} withIcon>
              <Icon name="close" />
            </Button>
          </div>
        )}

        {suffix && <div className={styles.fancySuffix}>{suffix}</div>}

        {endIcon && (
          <Button
            aria-label={endIconAriaLabel}
            className={styles.fancyInputIcon}
            color="transparent"
            onClick={onClickEndIcon}
            withIcon
          >
            <Icon name={endIcon} />
          </Button>
        )}
      </div>

      {helperText && (
        <div className={appearance && CLASS_TEXT_COLOR_MAP[appearance as FancyInputAppearanceType]}>{helperText}</div>
      )}
    </Fragment>
  );
});

export default memo(FancyInput);
